import type { Locale } from 'date-fns'
import { de as deDateFnsLocale, enUS } from 'date-fns/locale'
import i18n, { type TOptions } from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'

import de from '../translation_strings/de.json' assert { type: 'json' }
import en from '../translation_strings/en.json' assert { type: 'json' }

const LANGUAGE_STORAGE_KEY = 'ecoplanet_language'

export type SupportedLanguage = 'de' | 'en'

export const getSupportedLanguage = (language?: string | null): SupportedLanguage => {
  const normalizedLanguage = language?.split('-')[0]

  return normalizedLanguage === 'de' || normalizedLanguage === 'en' ? normalizedLanguage : 'de'
}

export const getDateFnsLocale = (language?: string | null): Locale => {
  return getSupportedLanguage(language) === 'en' ? enUS : deDateFnsLocale
}

const getStoredLanguage = (): SupportedLanguage | undefined => {
  const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? sessionStorage.getItem(LANGUAGE_STORAGE_KEY)

  return storedLanguage === 'de' || storedLanguage === 'en' ? storedLanguage : undefined
}

export const getInitialLanguage = (): SupportedLanguage => {
  return getStoredLanguage() ?? 'de'
}

export const configTranslation = (): void => {
  i18n.use(initReactI18next).init({
    defaultNS: 'app',
    debug: false,
    resources: {
      de,
      en,
    },
    lng: getInitialLanguage(),
    fallbackNS: ['app', 'fallback'],
    fallbackLng: ['de', 'en'],
    interpolation: {
      escapeValue: false,
    },
  })

  i18n.on('languageChanged', (lng: string) => {
    const supportedLanguage = getSupportedLanguage(lng)
    localStorage.setItem(LANGUAGE_STORAGE_KEY, supportedLanguage)
    sessionStorage.setItem(LANGUAGE_STORAGE_KEY, supportedLanguage)
  })
}

export const useTranslationService = (domain: string, screen: string) => {
  const { t } = useTranslation()

  const frequentTranslations = {
    tTitle: t(`app.${domain}.${screen}.title`),
    tDescription: t(`${domain}.${screen}.description`),
    tButtonCaption: t(`${domain}.${screen}.button_caption`),
  }

  const translate = <T extends Record<string, unknown>>(key: string, options?: TOptions<T>): string =>
    t(`${domain}.${screen}.${key}`, options) as string

  const common = (key: string): string => t(`common.${key}`)

  return {
    ...frequentTranslations,
    tCommon: common,
    t: translate,
    tGlobal: t,
  }
}
